import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { base44 } from '@/api/base44Client';
import { apiClient } from '@/api/apiClient';

const AuthContext = createContext(null);

/**
 * Self-hosted auth context.
 *
 * Replaces the Base44 redirect-based auth flow with a simple email/password
 * + JWT model. The token is stored in localStorage by `apiClient`.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Mirror the old API surface so existing UI doesn't have to branch.
  const isLoadingPublicSettings = false;
  const appPublicSettings = null;

  const checkAppState = useCallback(async () => {
    setIsLoadingAuth(true);
    setAuthError(null);

    if (!apiClient.getToken()) {
      setIsAuthenticated(false);
      setUser(null);
      setIsLoadingAuth(false);
      return;
    }

    try {
      const me = await base44.auth.me();
      setUser(me);
      setIsAuthenticated(true);
    } catch (err) {
      // 401 = stale or invalid token; clear it and ask the user to sign in.
      if (err.status === 401 || err.status === 403) {
        apiClient.setToken(null);
        setAuthError({ type: 'auth_required', message: 'Please sign in.' });
      } else {
        setAuthError({ type: 'unknown', message: err.message });
      }
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoadingAuth(false);
    }
  }, []);

  useEffect(() => {
    checkAppState();
  }, [checkAppState]);

  const login = useCallback(async (email, password) => {
    setAuthError(null);
    const res = await base44.auth.login(email, password);
    setUser(res.user);
    setIsAuthenticated(true);
    return res.user;
  }, []);

  const register = useCallback(async (data) => {
    setAuthError(null);
    const res = await base44.auth.register(data);
    setUser(res.user);
    setIsAuthenticated(true);
    return res.user;
  }, []);

  const logout = useCallback(() => {
    base44.auth.logout();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const navigateToLogin = useCallback(() => {
    base44.auth.redirectToLogin();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoadingAuth,
        isLoadingPublicSettings,
        authError,
        appPublicSettings,
        login,
        register,
        logout,
        navigateToLogin,
        checkAppState,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
